import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, BarChart3, TrendingUp, Award } from 'lucide-react';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface EvaluationReportWidgetProps {
  config: {
    reportType?: string;
  };
  size: WidgetSize;
}

const EvaluationReportWidget: React.FC<EvaluationReportWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    loadEvaluationData();
  }, [config.reportType, userProfile?.empresaCnpj]);

  const loadEvaluationData = async () => {
    if (!userProfile?.empresaCnpj || !config.reportType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const avaliacoes = await firebaseService.getAvaliacoes(userProfile.empresaCnpj);
      const alunos = await firebaseService.getAlunos(userProfile.empresaCnpj);
      const niveis = await firebaseService.getNiveis(userProfile.empresaCnpj);
      const professores = await firebaseService.getProfessores(userProfile.empresaCnpj);

      switch (config.reportType) {
        case 'taxa-aprovacao':
          setTitle('Taxa de Aprovação');
          const aprovadas = avaliacoes.filter(a => a.aprovado).length;
          const taxa = avaliacoes.length > 0 ? (aprovadas / avaliacoes.length) * 100 : 0;
          setData({ taxa, total: avaliacoes.length, aprovadas });
          break;
        case 'avaliacoes-recentes':
          setTitle('Avaliações Recentes');
          const recentes = avaliacoes
            .sort((a, b) => new Date(b.dataAvaliacao).getTime() - new Date(a.dataAvaliacao).getTime())
            .slice(0, 10)
            .map(av => {
              const aluno = alunos.find(a => a.id === av.alunoId);
              return {
                id: av.id,
                aluno: aluno?.nome || 'Não encontrado',
                nota: av.notaGeral,
                aprovado: av.aprovado,
                data: format(new Date(av.dataAvaliacao), 'dd/MM/yyyy', { locale: ptBR })
              };
            });
          setData(recentes);
          break;
        case 'evolucao-aprovacoes':
          setTitle('Evolução de Aprovações');
          const meses: { [key: string]: { total: number; aprovadas: number } } = {};
          avaliacoes.forEach(av => {
            const data = new Date(av.dataAvaliacao);
            const mes = `${data.getMonth() + 1}/${data.getFullYear()}`;
            if (!meses[mes]) {
              meses[mes] = { total: 0, aprovadas: 0 };
            }
            meses[mes].total++;
            if (av.aprovado) meses[mes].aprovadas++;
          });
          const evolucao = Object.entries(meses)
            .map(([mes, dados]) => ({
              mes,
              taxa: dados.total > 0 ? (dados.aprovadas / dados.total) * 100 : 0
            }))
            .sort((a, b) => a.mes.localeCompare(b.mes));
          setData(evolucao);
          break;
        case 'avaliacoes-por-nivel':
          setTitle('Avaliações por Nível');
          const porNivel: { [key: string]: number } = {};
          avaliacoes.forEach(av => {
            const nivel = niveis.find(n => n.id === av.nivelId);
            const nomeNivel = nivel?.nome || 'Sem nível';
            porNivel[nomeNivel] = (porNivel[nomeNivel] || 0) + 1;
          });
          setData(Object.entries(porNivel).map(([name, value]) => ({ name, value })));
          break;
        case 'avaliacoes-por-professor':
          setTitle('Avaliações por Professor');
          const porProfessor: { [key: string]: number } = {};
          avaliacoes.forEach(av => {
            const professor = professores.find(p => p.id === av.professorId);
            const nomeProfessor = professor?.nome || 'Não encontrado';
            porProfessor[nomeProfessor] = (porProfessor[nomeProfessor] || 0) + 1;
          });
          setData(Object.entries(porProfessor).map(([name, value]) => ({ name, value })));
          break;
        case 'media-notas-por-nivel':
          setTitle('Média de Notas por Nível');
          const notasPorNivel: { [key: string]: { soma: number; count: number } } = {};
          avaliacoes.forEach(av => {
            const nivel = niveis.find(n => n.id === av.nivelId);
            const nomeNivel = nivel?.nome || 'Sem nível';
            if (!notasPorNivel[nomeNivel]) {
              notasPorNivel[nomeNivel] = { soma: 0, count: 0 };
            }
            notasPorNivel[nomeNivel].soma += av.notaGeral || 0;
            notasPorNivel[nomeNivel].count++;
          });
          setData(Object.entries(notasPorNivel).map(([name, dados]) => ({
            name,
            media: dados.count > 0 ? dados.soma / dados.count : 0
          })));
          break;
        case 'top-10-alunos':
          setTitle('Top 10 Alunos');
          const alunosComNotas: { [key: string]: { soma: number; count: number; nome: string } } = {};
          avaliacoes.forEach(av => {
            const aluno = alunos.find(a => a.id === av.alunoId);
            if (aluno) {
              if (!alunosComNotas[av.alunoId]) {
                alunosComNotas[av.alunoId] = { soma: 0, count: 0, nome: aluno.nome };
              }
              alunosComNotas[av.alunoId].soma += av.notaGeral || 0;
              alunosComNotas[av.alunoId].count++;
            }
          });
          const topAlunos = Object.entries(alunosComNotas)
            .map(([id, dados]) => ({
              id,
              nome: dados.nome,
              media: dados.count > 0 ? dados.soma / dados.count : 0
            }))
            .sort((a, b) => b.media - a.media)
            .slice(0, 10);
          setData(topAlunos);
          break;
        default:
          setTitle('Relatório de Avaliações');
          setData(null);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de avaliações:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const maxItems = size === 'small' ? 3 : size === 'medium' ? 5 : 10;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0">
        {config.reportType === 'taxa-aprovacao' && data && (
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 mb-2">{data.taxa.toFixed(1)}%</div>
            <p className="text-sm text-gray-500">{data.aprovadas} de {data.total} avaliações</p>
          </div>
        )}
        {config.reportType === 'avaliacoes-recentes' && Array.isArray(data) && (
          <div className="space-y-2">
            {data.slice(0, maxItems).map((item: any) => (
              <div key={item.id} className="flex items-center justify-between p-2 border border-gray-100 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{item.aluno}</p>
                  <p className="text-xs text-gray-500">{item.data}</p>
                </div>
                <div className="flex items-center gap-2">
                  {item.aprovado ? (
                    <CheckCircle className="h-4 w-4 text-success-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-danger-600" />
                  )}
                  <span className="text-sm font-semibold text-gray-900">{(item.nota * 100).toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {Array.isArray(data) && data.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Nenhum dado disponível
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationReportWidget;

